import { useState } from 'react'
import { useToast } from '../ui/Toast.jsx'
import { errorMessage } from '../../lib/api.js'
import { fetchRecording, useSaveSybillKey, useSybillConnection, useTranscript } from '../../lib/queries/sybill.js'
import { fmtDate, fmtDateYear } from './fields.jsx'
import styles from './projects.module.css'

/**
 * Three steps, each shown only when the one before it is done:
 *   1. the organization's Sybill API key (set once, admins only),
 *   2. this project's Sybill reference (the meeting name),
 *   3. the meetings found for that reference, each opening in place.
 */
export function SybillTab({ project, api, canEdit }) {
  const connection = useSybillConnection()
  if (connection.isPending) return <p className={styles.muted}>Loading Sybill…</p>
  if (!connection.data?.connected) return <KeyStep canManage={Boolean(connection.data?.canManage)} />
  if (!project.sybill.linked) return <ReferenceStep project={project} api={api} canEdit={canEdit} />
  return <MeetingsStep project={project} api={api} canEdit={canEdit} />
}

function KeyStep({ canManage }) {
  const [key, setKey] = useState('')
  const save = useSaveSybillKey()
  return (
    <section className={styles.panel} style={{ maxWidth: 640 }}>
      <h2 className={styles.h}>Sybill API key (set once for all projects)</h2>
      <div className={styles.linkBox}>
        <span className={`${styles.pill} ${styles.pNone}`}>Not connected</span>
        <span className={styles.muted} style={{ flex: 1, minWidth: 180 }}>
          Sybill is not connected for your organization yet.
        </span>
      </div>
      {canManage ? (
        <form
          className={styles.formOne}
          style={{ marginTop: 14 }}
          onSubmit={(e) => {
            e.preventDefault()
            if (key.trim()) save.mutate(key.trim(), { onSuccess: () => setKey('') })
          }}
        >
          <p className={styles.note}>
            In Sybill, open Settings, Integrations, API Keys and create a key with the <b>Export</b> permission only. The key starts with <span className={styles.mono}>sk_live_</span> and is shown once.
          </p>
          <label className={styles.fieldLabel}>
            Sybill API key
            <input className={styles.input} type="password" autoComplete="off" value={key} maxLength={200} placeholder="sk_live_…" onChange={(e) => setKey(e.target.value)} />
          </label>
          <div>
            <button type="submit" className={styles.primary} disabled={!key.trim() || save.isPending}>
              {save.isPending ? 'Checking…' : 'Check and save key'}
            </button>
          </div>
          <p className={styles.note}>The key is checked with Sybill, then stored encrypted on the server. It is never shown again and is not kept on any project.</p>
        </form>
      ) : (
        <p className={styles.note} style={{ marginTop: 12 }}>
          An admin needs to add the Sybill API key here. Until then, type call notes in on the Weekly calls tab.
        </p>
      )}
    </section>
  )
}

function ReferenceStep({ project, api, canEdit }) {
  const [ref, setRef] = useState(project.sybill.ref)
  const [busy, setBusy] = useState(false)
  const link = async () => {
    setBusy(true)
    const linked = await api.patch(project.id, { sybill: { ref: ref.trim(), linked: true } })
    if (linked) await api.syncSybill(project.id)
    setBusy(false)
  }
  return (
    <section className={styles.panel} style={{ maxWidth: 640 }}>
      <h2 className={styles.h}>Sybill reference</h2>
      <div className={styles.linkBox}>
        <span className={`${styles.pill} ${styles.pOk}`}>Sybill connected</span>
        <span className={`${styles.pill} ${styles.pNone}`}>Not linked</span>
        <span className={styles.muted} style={{ flex: 1, minWidth: 180 }}>
          Enter this project's Sybill meeting name.
        </span>
      </div>
      <div className={styles.formOne} style={{ marginTop: 14 }}>
        <label className={styles.fieldLabel}>
          Meeting name in Sybill
          <input className={styles.input} value={ref} disabled={!canEdit} maxLength={80} placeholder="e.g. Harbor Supply weekly sync" onChange={(e) => setRef(e.target.value)} />
        </label>
        <div>
          <button type="button" className={styles.primary} disabled={!canEdit || !ref.trim() || busy} onClick={link}>
            {busy ? 'Searching Sybill…' : 'Link and pull meetings'}
          </button>
        </div>
        <p className={styles.note}>
          We search Sybill for meetings whose title contains this name and list every one we find. Use a specific name: a broad one like "weekly sync" would also pull in other projects' calls. New meetings are added within an hour of ending.
        </p>
      </div>
    </section>
  )
}

function MeetingsStep({ project, api, canEdit }) {
  const c = project.sybill
  const [syncing, setSyncing] = useState(false)
  const [openWeeks, setOpenWeeks] = useState(() => new Set(project.calls.filter((x) => x.source === 'sybill').slice(0, 1).map((x) => x.weekOf)))
  const meetings = project.calls.filter((call) => call.source === 'sybill')
  const toggle = (weekOf, isOpen) =>
    setOpenWeeks((set) => {
      const next = new Set(set)
      if (isOpen) next.add(weekOf)
      else next.delete(weekOf)
      return next
    })
  const sync = async () => {
    setSyncing(true)
    await api.syncSybill(project.id)
    setSyncing(false)
  }

  return (
    <>
      <div className={`${styles.row} ${styles.between}`}>
        <div className={styles.row}>
          <span className={`${styles.pill} ${styles.pOk}`}>Sybill connected</span>
          <span className={`${styles.pill} ${styles.pNone}`}>Linked to “{c.ref}”</span>
          {c.lastSyncedAt ? <span className={styles.muted}>Last checked {fmtDateYear(c.lastSyncedAt.slice(0, 10))}</span> : null}
        </div>
        {canEdit ? (
          <div className={styles.row}>
            <button type="button" className={styles.ghost} disabled={syncing} onClick={sync}>
              {syncing ? 'Checking…' : 'Sync now'}
            </button>
            <button type="button" className={styles.ghost} onClick={() => api.patch(project.id, { sybill: { linked: false } })}>
              Change reference
            </button>
          </div>
        ) : null}
      </div>

      {c.error ? (
        <p className={`${styles.note} ${styles.noteBad}`} role="alert">
          {c.error}
        </p>
      ) : null}

      <h2 className={styles.h} style={{ marginBottom: 0 }}>
        Weekly meetings ({meetings.length})
      </h2>
      {meetings.length === 0 ? (
        <div className={styles.empty}>No meetings found yet for “{c.ref}”. New ones appear within an hour of ending, or press Sync now.</div>
      ) : (
        meetings.map((call) => <Meeting key={call.id} project={project} call={call} api={api} canEdit={canEdit} open={openWeeks.has(call.weekOf)} onToggle={toggle} />)
      )}

      <section className={styles.panel}>
        <h2 className={styles.h}>Activity</h2>
        <div className={styles.log}>
          {c.log.length ? c.log.map((l, i) => <div key={i}>{`${l.at}  ${l.text}`}</div>) : <span className={styles.muted}>No activity yet.</span>}
        </div>
      </section>
    </>
  )
}

function Meeting({ project, call, api, canEdit, open, onToggle }) {
  const toast = useToast()
  const [playing, setPlaying] = useState(false)
  const play = async () => {
    setPlaying(true)
    try {
      const { url } = await fetchRecording(project.id, call.weekOf)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error(errorMessage(error, 'Could not get the recording.'))
    }
    setPlaying(false)
  }

  return (
    <details className={styles.week} open={open} onToggle={(e) => onToggle(call.weekOf, e.currentTarget.open)}>
      <summary>
        <span className={styles.weekName}>Week of {fmtDate(call.weekOf)}</span>
        <span className={`${styles.pill} ${styles.pRec}`}>From Sybill</span>
        <span className={styles.weekSummary}>{call.summary.split('\n')[0]}</span>
        <span className={`${styles.mono} ${styles.muted}`}>
          {fmtDate(call.heldOn)} · {call.actions.length} action item{call.actions.length === 1 ? '' : 's'}
        </span>
      </summary>
      <div className={styles.weekBody}>
        <div>
          <span className={styles.fieldLabel}>Attendees</span>
          <p style={{ margin: '2px 0 0' }}>{call.attendees || 'Not listed'}</p>
        </div>
        <div>
          <span className={styles.fieldLabel}>Summary</span>
          <p style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{call.summary || 'Sybill has not produced a summary for this call.'}</p>
        </div>
        <div>
          <div className={`${styles.row} ${styles.between}`} style={{ marginBottom: 4 }}>
            <span className={styles.fieldLabel}>Action items</span>
            <span className={`${styles.pill} ${styles.pNone}`}>Read from the call</span>
          </div>
          {call.actions.length === 0 ? <p className={styles.muted}>No action items found in this call.</p> : null}
          {call.actions.map((a) => (
            <div key={a.id} className={styles.action}>
              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                <div>{a.text}</div>
                <div className={styles.muted}>
                  Owner: {a.owner || 'Not stated'} · {a.dueOn ? `Due ${fmtDate(a.dueOn)}` : 'No date'}
                </div>
              </div>
              {a.moved ? (
                <span className={`${styles.pill} ${styles.pOk}`}>In Overview</span>
              ) : canEdit ? (
                <button type="button" className={`${styles.btn} ${styles.sm}`} onClick={() => api.moveAction(project.id, call.weekOf, a.id)}>
                  Move to Overview
                </button>
              ) : null}
            </div>
          ))}
        </div>
        {canEdit ? (
          <>
            <div className={styles.row}>
              <button type="button" className={styles.ghost} disabled={playing} onClick={play}>
                {playing ? 'Opening…' : 'Play recording'}
              </button>
              <span className={styles.note}>A fresh link is requested each time, because Sybill links expire after 24 hours.</span>
            </div>
            {call.hasTranscript && open ? <Transcript projectId={project.id} weekOf={call.weekOf} /> : null}
          </>
        ) : (
          <p className={styles.note}>Recordings and transcripts are visible to editors.</p>
        )}
      </div>
    </details>
  )
}

const clock = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '')

function Transcript({ projectId, weekOf }) {
  const { data, isPending, error } = useTranscript(projectId, weekOf, true)
  return (
    <div>
      <span className={styles.fieldLabel}>Transcript</span>
      {isPending ? <p className={styles.muted}>Loading transcript…</p> : null}
      {error ? <p className={styles.muted}>{errorMessage(error, 'Could not load the transcript.')}</p> : null}
      {data ? (
        <div className={styles.transcript} tabIndex={0} aria-label="Transcript">
          {data.map((line, i) => (
            <p key={i}>
              <span className={styles.stamp}>{clock(line.at)}</span> <b>{line.speaker || 'Speaker'}:</b> {line.text}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  )
}
